import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";

const SESSION_KEY = "bizwell_analytics_session";
const LAST_TRACKED_KEY = "bizwell_last_tracked";

// Bot detection patterns
const BOT_PATTERNS = [
  /bot/i, /crawler/i, /spider/i, /scrape/i, /lighthouse/i,
  /pingdom/i, /googlebot/i, /bingbot/i, /yandex/i, /baidu/i,
  /facebookexternalhit/i, /twitterbot/i, /linkedinbot/i,
  /headless/i, /phantom/i, /selenium/i, /puppeteer/i,
  /ahrefs/i, /semrush/i, /moz/i,
];

function isBot(): boolean {
  const ua = navigator.userAgent;
  return BOT_PATTERNS.some((p) => p.test(ua));
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getSessionId(): string {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

function getIpHash(): string {
  // We can't get real IP client-side, use a hash of user-agent + screen size + language as pseudo-identifier
  const raw = `${navigator.userAgent}|${screen.width}x${screen.height}|${navigator.language}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const char = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return `ip_${Math.abs(hash)}`;
}

// Debounce tracking to avoid duplicate counts on re-renders / route flapping
const DEBOUNCE_MS = 2000;
let trackTimeout: ReturnType<typeof setTimeout> | null = null;

async function upsertSession(sessionId: string, pagePath: string) {
  const ipHash = getIpHash();
  const device = getDeviceType();

  // Check if session exists
  const { data } = await supabase
    .from("user_sessions")
    .select("page_count")
    .eq("session_id", sessionId)
    .maybeSingle();

  if (data) {
    // Update existing session
    await supabase
      .from("user_sessions")
      .update({
        end_time: new Date().toISOString(),
        page_count: (data.page_count || 1) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq("session_id", sessionId);
  } else {
    // Insert new session
    await supabase.from("user_sessions").insert({
      session_id: sessionId,
      landing_page: pagePath,
      device_type: device,
      ip_hash: ipHash,
      referrer: document.referrer || null,
      page_count: 1,
    });
  }
}

async function trackPageView(pagePath: string, pageTitle?: string) {
  if (isBot()) return;

  const last = sessionStorage.getItem(LAST_TRACKED_KEY);
  const now = Date.now();
  if (last) {
    const [lastPath, lastTime] = last.split("|");
    if (lastPath === pagePath && now - parseInt(lastTime || "0", 10) < DEBOUNCE_MS) {
      return; // Skip duplicate within debounce window
    }
  }
  sessionStorage.setItem(LAST_TRACKED_KEY, `${pagePath}|${now}`);

  const sessionId = getSessionId();
  const ipHash = getIpHash();
  const device = getDeviceType();

  // Insert page view
  await supabase.from("page_views").insert({
    page_path: pagePath,
    page_title: pageTitle || document.title,
    session_id: sessionId,
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
    ip_hash: ipHash,
    device_type: device,
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    language: navigator.language,
  });

  // Update session
  await upsertSession(sessionId, pagePath);
}

// Exported helpers for manual tracking
export async function trackProductView(productId: string, productName: string, companySlug: string, productSlug: string, pagePath: string) {
  if (isBot()) return;
  const sessionId = getSessionId();
  const ipHash = getIpHash();
  await supabase.from("product_views").insert({
    product_id: productId,
    product_name: productName,
    company_slug: companySlug,
    product_slug: productSlug,
    page_path: pagePath,
    session_id: sessionId,
    ip_hash: ipHash,
  });
}

export async function trackEventView(eventId: string, eventType: string, eventTitle: string, pagePath: string) {
  if (isBot()) return;
  const sessionId = getSessionId();
  const ipHash = getIpHash();
  await supabase.from("event_views").insert({
    event_id: eventId,
    event_type: eventType,
    event_title: eventTitle,
    page_path: pagePath,
    session_id: sessionId,
    ip_hash: ipHash,
  });
}

export async function trackClickEvent(elementId: string, elementName: string, elementText: string, pagePath: string) {
  if (isBot()) return;
  const sessionId = getSessionId();
  const ipHash = getIpHash();
  await supabase.from("click_events").insert({
    element_id: elementId,
    element_name: elementName,
    element_text: elementText,
    page_path: pagePath,
    session_id: sessionId,
    ip_hash: ipHash,
  });
}

// Hook for automatic route tracking
export function useAnalyticsTracking() {
  const location = useLocation();
  const lastPathRef = useRef<string>("");

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPathRef.current) return;
    lastPathRef.current = path;

    if (trackTimeout) clearTimeout(trackTimeout);
    trackTimeout = setTimeout(() => {
      trackPageView(path, document.title);
    }, 300); // Small delay to let page title settle after route change

    return () => {
      if (trackTimeout) clearTimeout(trackTimeout);
    };
  }, [location.pathname, location.search]);
}

export default useAnalyticsTracking;